import { ArrowUpRight } from "lucide-react";
import { SITE, WA_DEFAULT_MESSAGE, whatsappLink } from "@/config/site";
import { Reveal } from "@/components/motion";
import { trackEvent } from "@/lib/analytics";

export const WhatsAppIcon = ({ className = "h-5 w-5" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

export const BookingButton = ({ label = "Prenota online", dark = false, testId = "booking-button", className = "" }) => (
  <a
    href={SITE.bookingUrl}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testId}
    onClick={() => trackEvent("booking_click", { cta: label })}
    className={`group inline-flex items-center gap-3 rounded-full px-8 py-4 text-sm font-bold uppercase tracking-[0.14em] transition-colors duration-300 ${
      dark
        ? "bg-white text-ink hover:bg-leaf hover:text-ink"
        : "bg-ink text-white hover:bg-leaf-deep"
    } ${className}`}
  >
    {label}
    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
  </a>
);

export const WhatsAppButton = ({ label = "Scrivimi su WhatsApp", dark = false, testId = "whatsapp-button", className = "" }) => (
  <a
    href={whatsappLink(WA_DEFAULT_MESSAGE)}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testId}
    onClick={() => trackEvent("whatsapp_click", { cta: label })}
    className={`group inline-flex items-center gap-3 rounded-full border px-8 py-4 text-sm font-bold uppercase tracking-[0.14em] transition-colors duration-300 ${
      dark
        ? "border-white/25 text-white hover:border-leaf hover:text-leaf"
        : "border-ink/20 text-ink hover:border-leaf-deep hover:text-leaf-deep"
    } ${className}`}
  >
    <WhatsAppIcon className="h-4 w-4" />
    {label}
  </a>
);

// Blocco riutilizzabile: assistenza WhatsApp vicino alle CTA di prenotazione.
export const WhatsAppAssist = ({ dark = false, testId = "whatsapp-assist" }) => (
  <div
    data-testid={testId}
    className={`rounded-2xl border p-6 sm:p-7 ${
      dark ? "border-white/15 bg-white/5" : "border-ink/10 bg-mist"
    }`}
  >
    <p className={`font-display text-xl tracking-tight ${dark ? "text-white" : "text-ink"}`}>
      Hai difficoltà con la prenotazione online?
    </p>
    <p className={`mt-2 text-sm leading-relaxed ${dark ? "text-white/60" : "text-ink/60"}`}>
      Scrivimi su WhatsApp indicando, se possibile, la sede preferita (Bologna o Casalecchio di
      Reno) e orientativamente i giorni e le fasce orarie in cui sei disponibile: ti aiuterò
      personalmente a fissare l'appuntamento.
    </p>
    <div className="mt-5">
      <WhatsAppButton dark={dark} testId={`${testId}-button`} />
    </div>
    <p className={`mt-4 text-[11px] leading-relaxed ${dark ? "text-white/35" : "text-ink/40"}`}>
      WhatsApp è pensato per assistenza alla prenotazione, domande organizzative e informazioni sui
      servizi. Ti chiedo di non inviare dati sanitari o informazioni mediche sensibili tramite chat.
    </p>
  </div>
);

// Sezione di conversione principale, usata in Home e adattabile altrove.
export const BookingSection = ({
  title = "Vuoi iniziare un percorso personalizzato?",
  text = "Prenota una prima visita e valuteremo insieme il percorso più adatto alle tue esigenze.",
}) => (
  <section className="grain relative overflow-hidden bg-ink text-white">
    <div
      aria-hidden="true"
      className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full border border-white/10"
    />
    <div
      aria-hidden="true"
      className="pointer-events-none absolute -bottom-40 -left-24 h-[28rem] w-[28rem] rounded-full border border-white/10"
    />
    <div className="relative mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32">
      <div className="grid gap-14 lg:grid-cols-[1.5fr_1fr] lg:items-center">
        <Reveal>
          <p className="eyebrow text-leaf">Prenotazione</p>
          <h2 className="mt-6 max-w-xl font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.08] tracking-tight text-balance">
            {title}
          </h2>
          <p className="mt-6 max-w-md text-base md:text-lg leading-relaxed text-white/65">{text}</p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <BookingButton dark testId="booking-section-cta" />
            <WhatsAppButton dark testId="booking-section-whatsapp" />
          </div>
          <p className="mt-6 text-xs text-white/40">
            La prenotazione online avviene tramite MioDottore, in autonomia e in pochi minuti.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <WhatsAppAssist dark testId="booking-section-assist" />
        </Reveal>
      </div>
    </div>
  </section>
);

// Azioni flottanti: barra di prenotazione sticky su mobile,
// pulsante WhatsApp discreto su desktop.
export const FloatingActions = () => (
  <>
    <div
      data-testid="mobile-sticky-booking-bar"
      className="fixed inset-x-0 bottom-0 z-40 flex items-center gap-2 border-t border-ink/10 bg-white/90 p-3 backdrop-blur-xl md:hidden"
    >
      <a
        href={SITE.bookingUrl}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="mobile-sticky-booking-button"
        onClick={() => trackEvent("booking_click", { cta: "sticky-mobile" })}
        className="flex-1 rounded-full bg-ink px-4 py-3.5 text-center text-xs font-bold uppercase tracking-[0.14em] text-white"
      >
        Prenota una visita
      </a>
      <a
        href={whatsappLink(WA_DEFAULT_MESSAGE)}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="mobile-sticky-whatsapp-button"
        onClick={() => trackEvent("whatsapp_click", { cta: "sticky-mobile" })}
        aria-label="Scrivimi su WhatsApp"
        className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#25D366] text-white"
      >
        <WhatsAppIcon className="h-5 w-5" />
      </a>
    </div>
    <a
      href={whatsappLink(WA_DEFAULT_MESSAGE)}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="floating-whatsapp-button"
      aria-label="Scrivimi su WhatsApp"
      className="fixed bottom-6 right-6 z-40 hidden md:inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-lg shadow-black/10 transition-transform duration-300 hover:scale-105"
    >
      <WhatsAppIcon className="h-6 w-6" />
    </a>
  </>
);
